import React from "react";
import { Card, CardBody, Button, Row, Col, Badge } from "reactstrap";
import { connect } from "react-redux";
import CoreEngine from "../../core/CoreEngine";
import RequestEngine from "../../core/RequestEngine";
import loadingAction from "../../core/redux/actions/loadingAction";
import strings from "../../core/translate";
import { Scopes } from "../../shared/scopes.enum";
import { FormModal } from "../../components/Form/FormKit";
import GarageSetupForm from "./GarageSetupForm";
import GarageSectionHeader from "../../components/Garage/GarageSectionHeader";
import EmptyState from "../../components/Page/EmptyState";
import BranchPhotosPanel from "../merchants-branches-photos/BranchPhotosPanel";
import GarageTiersPanel from "./GarageTiersPanel";
import { hasAnyScope } from "../../shared/permissions";

class GarageSetupList extends CoreEngine {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      setup: null,
      selectedBranch: "",
      selectedMerchant: "",
      branchName: "",
      formOpen: false,
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    const { selectedBranch } = this.props.match.params;
    const routeState = this.props.location.state || {};
    this.setState(
      {
        selectedBranch,
        branchName: routeState.branchName || "",
        selectedMerchant: routeState.selectedMerchant || "",
      },
      this.callPage
    );
  }

  get pathname() {
    return `merchants-branches/${this.state.selectedBranch}/garage-setup`;
  }

  callPage = async () => {
    const { selectedBranch } = this.state;
    if (!selectedBranch) {
      this.setState({ isLoading: false });
      return;
    }
    const response = await this.engine.getItem(this.pathname);
    if (response && response.status === 200) {
      const items = response.data.items || [];
      this.setState({ isLoading: false, setup: items[0] || null });
    } else {
      this.setState({ isLoading: false });
    }
    this.props.loadingAction(false);
  };

  handleOpenForm = () => this.setState({ formOpen: true });
  handleCloseForm = () => this.setState({ formOpen: false });
  handleSaved = () => this.setState({ formOpen: false }, this.callPage);

  renderSummary(setup, canWrite) {
    const paymentMethodNames = (setup.paymentMethods || [])
      .map((it) => it.name)
      .join(", ");

    return (
      <Card>
        <CardBody>
          <Row className="mb-3">
            <Col md="3" className="text-muted">
              {strings.currency}
            </Col>
            <Col md="9">
              {setup.currency
                ? `${setup.currency.name} (${setup.currency.symbol})`
                : "—"}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md="3" className="text-muted">
              {strings.vat}
            </Col>
            <Col md="9">
              {setup.hasVat
                ? `${strings.yes} — ${setup.vatPercentage}%`
                : strings.no}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md="3" className="text-muted">
              {strings.delivery_colors}
            </Col>
            <Col md="9">
              <Badge color="success" className="mr-2">
                {strings.green_threshold_hours}: {setup.greenThresholdHours}h
              </Badge>
              <Badge color="warning" className="mr-2">
                {strings.yellow_threshold_hours}: {setup.yellowThresholdHours}h
              </Badge>
              <Badge color="danger">
                {strings.red_threshold_hours}: {setup.redThresholdHours}h
              </Badge>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md="3" className="text-muted">
              {strings.managers_can_price}
            </Col>
            <Col md="9">
              {setup.managersCanPrice === false ? strings.no : strings.yes}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md="3" className="text-muted">
              {strings.accepted_payment_methods}
            </Col>
            <Col md="9">{paymentMethodNames || "—"}</Col>
          </Row>
          {canWrite && (
            <Button color="primary" onClick={this.handleOpenForm}>
              {strings.edit}
            </Button>
          )}
        </CardBody>
      </Card>
    );
  }

  renderEmpty(canWrite) {
    return (
      <Card>
        <CardBody>
          <EmptyState
            icon="nc-icon nc-settings-gear-65"
            title={strings.no_garage_setup}
            text={strings.no_garage_setup_hint}
          >
            {canWrite && (
              <Button color="primary" onClick={this.handleOpenForm}>
                {strings.add}
              </Button>
            )}
          </EmptyState>
        </CardBody>
      </Card>
    );
  }

  render() {
    const {
      isLoading,
      setup,
      selectedBranch,
      selectedMerchant,
      branchName,
      formOpen,
    } = this.state;
    const canWrite = this.hasPermission([Scopes.GARAGE_SETUP_WRITE]);
    // photos are no longer their own menu entry: they live with the rest of a
    // garage's setup
    const canReadPhotos = hasAnyScope([
      Scopes.MERCHANTS_BRANCHES_PHOTOS_READ,
      Scopes.MERCHANTS_BRANCHES_PHOTOS_WRITE,
    ]);

    return (
      <div className="content">
        <GarageSectionHeader
          branchId={selectedBranch}
          branchName={branchName}
          merchantId={selectedMerchant}
          active="garage-setup"
          history={this.props.history}
        />
        <Row>
          <Col md="12">
            {isLoading ? (
              this.renderProgress()
            ) : setup ? (
              this.renderSummary(setup, canWrite)
            ) : (
              this.renderEmpty(canWrite)
            )}
          </Col>
        </Row>

        {/* staff tiers and their hourly rates, priced in the garage's
            currency; each member of staff is placed on one */}
        {!isLoading && selectedBranch && (
          <Row>
            <Col md="12">
              <GarageTiersPanel
                branchId={selectedBranch}
                branchName={branchName}
                canWrite={canWrite}
                currencySymbol={
                  setup && setup.currency ? setup.currency.symbol : ""
                }
              />
            </Col>
          </Row>
        )}

        {canReadPhotos && selectedBranch && (
          <Row>
            <Col md="12">
              <BranchPhotosPanel
                branchId={selectedBranch}
                branchName={branchName}
              />
            </Col>
          </Row>
        )}

        <FormModal
          isOpen={formOpen}
          onClose={this.handleCloseForm}
          icon="nc-icon nc-settings-gear-65"
          title={strings.garage_setup}
          subtitle={branchName}
        >
          <GarageSetupForm
            key={(setup && setup.id) || "new"}
            item={setup}
            pathname={this.pathname}
            onSaved={this.handleSaved}
            onCancel={this.handleCloseForm}
          />
        </FormModal>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  loadingAction: (payload) => dispatch(loadingAction(payload)),
});

export default connect(null, mapDispatchToProps)(GarageSetupList);
